/**
 * Sprint T-16.1 · Automation Center · Dialogs.
 *
 * RunJobDialog · PauseJobDialog · DlqRetryDialog · DlqDiscardDialog.
 * All modals are controlled (open/onClose props · action delegated via callbacks).
 *
 * JobDetailPanel lives in a separate file (larger).
 */
import React, { useState } from "react";
import { X, Play, Pause, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import JsonPreview from "./JsonPreview";
import { humanReadableSchedule } from "./Formatters";

// ============================================================
// Base modal shell (dumb)
// ============================================================

function ModalShell({ title, onClose, children, testId, footer }) {
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-lg overflow-hidden rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-[var(--ag-border)] px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--ag-text-dim)]">
            {title}
          </span>
          <button
            type="button"
            data-testid={`${testId}-close`}
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] p-1 hover:bg-[var(--ag-surface-2)]"
          >
            <X className="h-3 w-3" />
          </button>
        </header>
        <div className="max-h-[65vh] overflow-y-auto p-4">{children}</div>
        {footer && <footer className="border-t border-[var(--ag-border)] px-3 py-2">{footer}</footer>}
      </div>
    </div>
  );
}


// ============================================================
// RunJobDialog
// ============================================================

export function RunJobDialog({ job, onClose, onConfirm, pending }) {
  const [inputRaw, setInputRaw] = useState("{}");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [parseError, setParseError] = useState(null);

  const submit = () => {
    let parsed = {};
    if (inputRaw.trim() && inputRaw.trim() !== "{}") {
      try {
        parsed = JSON.parse(inputRaw);
      } catch (e) {
        setParseError("JSON inválido: " + (e.message || "parse error"));
        return;
      }
    }
    onConfirm?.({ input: parsed, idempotency_key: idempotencyKey.trim() || undefined });
  };

  return (
    <ModalShell
      title={`Ejecutar · ${job.name}`}
      onClose={onClose}
      testId="run-job-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="run-job-cancel"
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] px-3 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="run-job-confirm"
            onClick={submit}
            disabled={pending}
            className="inline-flex items-center gap-1 rounded bg-emerald-500 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
          >
            <Play className="h-3 w-3" /> {pending ? "Enviando…" : "Ejecutar ahora"}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)]/40 p-2 text-[11px]">
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Job</div>
          <div className="mt-0.5 font-mono text-xs font-semibold">{job.name}</div>
          <div className="mt-0.5 text-[10px] text-[var(--ag-text-dim)]">{job.description}</div>
        </div>

        <div>
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">
            idempotency_key (opcional)
          </label>
          <input
            data-testid="run-job-idempotency"
            type="text"
            value={idempotencyKey}
            onChange={(e) => setIdempotencyKey(e.target.value)}
            placeholder="Auto-generado si vacío"
            className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs"
          />
        </div>

        <div>
          <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">
            input JSON (opcional)
          </label>
          <textarea
            data-testid="run-job-input"
            value={inputRaw}
            onChange={(e) => {
              setInputRaw(e.target.value);
              setParseError(null);
            }}
            rows={6}
            className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs"
          />
          {parseError && (
            <div data-testid="run-job-error" className="mt-1 text-[10px] text-red-500">
              {parseError}
            </div>
          )}
        </div>

        {job.idempotency_window_seconds > 0 && (
          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-[10px] text-amber-500">
            Este job usa ventana de idempotencia de {job.idempotency_window_seconds}s ·
            si se colisiona el mismo idempotency_key, el backend devolverá la ejecución existente.
          </div>
        )}
      </div>
    </ModalShell>
  );
}


// ============================================================
// PauseJobDialog
// ============================================================

const PAUSE_PRESETS = [
  { label: "1 hora", seconds: 3600 },
  { label: "1 día", seconds: 24 * 3600 },
  { label: "1 semana", seconds: 7 * 24 * 3600 },
  { label: "Indefinido", seconds: null },
];

export function PauseJobDialog({ job, onClose, onConfirm, pending }) {
  const [choice, setChoice] = useState(PAUSE_PRESETS[1]);

  const submit = () => {
    let until = null;
    if (choice.seconds != null) {
      until = new Date(Date.now() + choice.seconds * 1000).toISOString();
    }
    onConfirm?.({ until });
  };

  return (
    <ModalShell
      title={`Pausar · ${job.name}`}
      onClose={onClose}
      testId="pause-job-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="pause-job-cancel"
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] px-3 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="pause-job-confirm"
            onClick={submit}
            disabled={pending}
            className="inline-flex items-center gap-1 rounded bg-amber-500 px-3 py-1 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50"
          >
            <Pause className="h-3 w-3" /> {pending ? "Enviando…" : "Confirmar pausa"}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-[var(--ag-text-dim)]">
          El job dejará de programarse. Ejecuciones ya encoladas se ejecutarán igualmente.
        </p>
        <div>
          <div className="mb-1 text-[11px] text-[var(--ag-text-dim)]">Duración</div>
          <div className="flex flex-wrap gap-1.5" data-testid="pause-job-presets">
            {PAUSE_PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                data-testid={`pause-job-preset-${p.label}`}
                onClick={() => setChoice(p)}
                className={`rounded border px-2 py-1 text-[10px] ${
                  choice.label === p.label
                    ? "border-amber-500/60 bg-amber-500/15 text-amber-500"
                    : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)]/40 p-2 text-[10px]">
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Schedule actual</div>
          <div className="mt-0.5">{humanReadableSchedule(job.schedule)}</div>
        </div>
      </div>
    </ModalShell>
  );
}


// ============================================================
// DlqRetryDialog + DlqDiscardDialog
// ============================================================

export function DlqRetryDialog({ entry, onClose, onConfirm, pending }) {
  return (
    <ModalShell
      title={`Reintentar DLQ · ${entry.job_name}`}
      onClose={onClose}
      testId="dlq-retry-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="dlq-retry-cancel"
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] px-3 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="dlq-retry-confirm"
            onClick={onConfirm}
            disabled={pending}
            className="inline-flex items-center gap-1 rounded bg-emerald-500 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
          >
            <RefreshCw className="h-3 w-3" /> {pending ? "Enviando…" : "Reintentar"}
          </button>
        </div>
      }
    >
      <div className="space-y-2 text-[11px]">
        <p>Se creará una nueva ejecución con el mismo input original.</p>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Último error</div>
          <div className="rounded border border-red-500/30 bg-red-500/5 p-2 text-[10px]">
            <code className="font-mono text-red-500">{entry.last_error?.type}</code>
            <span className="ml-2">{(entry.last_error?.message || "").slice(0, 240)}</span>
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Input original</div>
          <JsonPreview data={entry.input} maxChars={200} testId="dlq-retry-input" label="input" />
        </div>
      </div>
    </ModalShell>
  );
}

export function DlqDiscardDialog({ entry, onClose, onConfirm, pending }) {
  return (
    <ModalShell
      title={`Descartar DLQ · ${entry.job_name}`}
      onClose={onClose}
      testId="dlq-discard-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="dlq-discard-cancel"
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] px-3 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="dlq-discard-confirm"
            onClick={onConfirm}
            disabled={pending}
            className="inline-flex items-center gap-1 rounded border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs text-red-500 hover:bg-red-500/20 disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" /> {pending ? "Enviando…" : "Descartar"}
          </button>
        </div>
      }
    >
      <div className="space-y-2 text-[11px]">
        <p>La entrada quedará marcada como descartada · el audit log conserva la trazabilidad.</p>
        <p className="text-[var(--ag-text-dim)]">
          Esta acción no es reversible desde la UI, pero el registro permanece consultable en el
          histórico del engine (audit T-14).
        </p>
      </div>
    </ModalShell>
  );
}
